import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Order } from '@components/table/types';
import { showGenericErrorToast } from '@utils/error';

import { useLazyGetTeamMembersQuery } from '../api';
import { formatTeamMembers } from '../services/teamMember';
import { TeamMemberTableLimit } from '../constants';

export type TeamMembersTableHeader =
  | 'name'
  | 'email'
  | 'secondaryEmail'
  | 'designation'
  | 'description'
  | 'linkedinUrl';

export type TeamMembersTableData = {
  id: string;
  name: string;
  designation: string;
  description: string;
  userId: string;
  role: string;
  email: string;
  secondaryEmail: string;
  linkedinUrl: string;
  coverImagePath: string;
  profileImagePath: string;
  createdAt: string;
  order: number;
};

const useGetTeamMembers = () => {
  const [sortOrder, setSortOrder] = useState<Order>('asc');
  const [sortBy, setSortBy] = useState<TeamMembersTableHeader | ''>('');
  const [limit, setLimit] = useState(TeamMemberTableLimit);
  const [offset, setOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const { companyId } = useParams();

  const [teamMembersData, setTeamMembersData] = useState<
    TeamMembersTableData[]
  >([]);

  const [debouncedSearchText] = useDebounce(searchTerm, 300);

  const [getTeamMembers, { data, isLoading }] = useLazyGetTeamMembersQuery();

  const getMembers = async () => {
    try {
      const d = await getTeamMembers({
        companyId: companyId ?? '',
        payload: {
          limit,
          offset: offset,
          // order: sortOrder,
          search: debouncedSearchText
          // sort: sortBy && sortByKeyMap[sortBy] ? sortByKeyMap[sortBy] : ''
        }
      }).unwrap();

      if (offset === 0) {
        setTeamMembersData(formatTeamMembers(d));
      } else {
        const newTeamMembers = formatTeamMembers(d);
        setTeamMembersData([...teamMembersData, ...newTeamMembers]);
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  useEffect(() => {
    getMembers();
  }, [sortBy, sortOrder, limit, offset, data]);

  useEffect(() => {
    if (offset) {
      setOffset(0);
    } else {
      getMembers();
    }
  }, [debouncedSearchText]);

  return {
    sortOrder,
    setSortOrder,
    sortBy,
    setSortBy,
    limit,
    offset,
    setLimit,
    setOffset,
    setSearchTerm,
    searchTerm,
    teamMembersData,
    totalRows: data?.total_count ?? 0,
    maxOrder: data?.max_order ?? 0,
    isLoading
  };
};

export default useGetTeamMembers;
